/**
 * D33/D40 (follow-up of P06) — what the operator reads when the backend
 * refuses to delete a station, a route or a city, or to create a separate
 * price category, because a season is still open (P06 refusal codes), in the
 * three operator languages (RO/RU/EN).
 *
 * SELF-CONTAINED, like the Punctuality (X15) and Web Push (X17) catalogs: NOT
 * registered in the application i18n and it does not import
 * `@/core/plugins/i18n` (shared file, untouched).
 *
 * Locale sources, read at render time:
 *  - components: the application's reactive `$i18n.locale` (vue-i18n global
 *    injection), falling back to the stored choice when no plugin is installed;
 *  - outside a component (the global `catchErrors`): the stored choice,
 *    localStorage `lang`, which the header language menu writes.
 * Nothing stored = the application default `en`. Any other value but
 * `ro | ru | en` resolves to `ro`.
 *
 * Text precedence (D33/D40): the backend sends ONE Romanian operator sentence
 * (`message`, P06 contract) and leaves RU/EN to the UI. So:
 *  - RO: the server's own sentence when present, else the specific RO text;
 *  - RU / EN: the specific text in the reader's language, and the server's
 *    Romanian sentence below it, marked as such (`lang="ro"`) — never a
 *    generic "error".
 * The seasons (`details.window_ids`) and where to close them are added by the
 * UI in every language. Interpolation is a single pass of `{name}` → value.
 */
import {computed, getCurrentInstance, unref, type ComputedRef} from "vue";
import type {SeasonRefusal} from "@/utils/seasonRefusal";

export const SEASON_REFUSAL_LOCALES = ["ro", "ru", "en"] as const;
export type SeasonRefusalLocale = typeof SEASON_REFUSAL_LOCALES[number];

export const resolveSeasonRefusalLocale = (value: unknown): SeasonRefusalLocale => {
  const code = String(value || "").toLowerCase().split(/[-_]/)[0];
  return (SEASON_REFUSAL_LOCALES as readonly string[]).includes(code) ? (code as SeasonRefusalLocale) : "ro";
};

const ro = {
  "title.stop": "Stația nu a fost ștearsă",
  "title.stopNamed": "Stația „{name}” nu a fost ștearsă",
  "title.route": "Ruta nu a fost ștearsă",
  "title.routeNamed": "Ruta „{name}” nu a fost ștearsă",
  "title.city": "Orașul nu a fost șters",
  "title.cityNamed": "Orașul „{name}” nu a fost șters",
  "title.category": "Categoria separată nu a fost creată",
  "title.categoryNamed": "Categoria separată pentru ruta „{name}” nu a fost creată",

  "text.stop": "Stația are tarife într-un sezon activ. Închideți mai întâi sezonul, apoi ștergeți stația. Nimic nu a fost modificat.",
  "text.route": "Ruta are tarife într-un sezon activ. Închideți mai întâi sezonul, apoi ștergeți ruta. Nimic nu a fost modificat.",
  "text.city": "Orașul este stație pe o rută cu tarife într-un sezon activ. Închideți mai întâi sezonul, apoi ștergeți orașul. Nimic nu a fost modificat.",
  "text.category": "Categoria are tarife într-un sezon activ pe această rută. Închideți mai întâi sezonul, apoi creați categoria separată. Nimic nu a fost modificat.",
  "text.retry": "Serverul este ocupat. Încercați din nou. Nimic nu a fost modificat.",
  "text.scope": "Ruta s-a schimbat între timp. Reîncărcați pagina și încercați din nou. Nimic nu a fost modificat.",

  "seasons.list": "Sezoane active: {list}",
  "seasons.more": "{list} și încă {count}",

  "hint.stop": "Sezoanele se închid în tab-ul „Preturi”, secțiunea „Tarife sezoniere”.",
  "hint.route": "Deschideți ruta: sezoanele se închid în tab-ul „Preturi”, secțiunea „Tarife sezoniere”.",
  "hint.city": "Deschideți rutele care trec prin acest oraș: sezoanele se închid în tab-ul „Preturi”, secțiunea „Tarife sezoniere”.",

  "server.label": "Mesajul serverului (în română):",

  "action.openPrices": "Deschide „Preturi”",
  "action.openRoute": "Deschide ruta #{id}",
  "action.close": "Închide",
  "action.closeAria": "Închide mesajul",

  "generic.error": "A apărut o eroare. Încercați din nou.",
  "generic.network": "Serverul nu a putut fi contactat. Verificați conexiunea și încercați din nou.",
};

export type SeasonRefusalMessageKey = keyof typeof ro;
type SeasonRefusalCatalog = Record<SeasonRefusalMessageKey, string>;

const ru: SeasonRefusalCatalog = {
  "title.stop": "Остановка не удалена",
  "title.stopNamed": "Остановка «{name}» не удалена",
  "title.route": "Маршрут не удалён",
  "title.routeNamed": "Маршрут «{name}» не удалён",
  "title.city": "Город не удалён",
  "title.cityNamed": "Город «{name}» не удалён",
  "title.category": "Отдельная категория не создана",
  "title.categoryNamed": "Отдельная категория для маршрута «{name}» не создана",

  "text.stop": "У остановки есть тарифы в действующем сезоне. Сначала закройте сезон, затем удалите остановку. Ничего не изменено.",
  "text.route": "У маршрута есть тарифы в действующем сезоне. Сначала закройте сезон, затем удалите маршрут. Ничего не изменено.",
  "text.city": "Город — остановка на маршруте с тарифами в действующем сезоне. Сначала закройте сезон, затем удалите город. Ничего не изменено.",
  "text.category": "У категории есть тарифы в действующем сезоне на этом маршруте. Сначала закройте сезон, затем создайте отдельную категорию. Ничего не изменено.",
  "text.retry": "Сервер занят. Попробуйте ещё раз. Ничего не изменено.",
  "text.scope": "Маршрут тем временем изменился. Обновите страницу и попробуйте ещё раз. Ничего не изменено.",

  "seasons.list": "Действующие сезоны: {list}",
  "seasons.more": "{list} и ещё {count}",

  "hint.stop": "Сезоны закрываются на вкладке «Preturi», в разделе «Сезонные тарифы».",
  "hint.route": "Откройте маршрут: сезоны закрываются на вкладке «Preturi», в разделе «Сезонные тарифы».",
  "hint.city": "Откройте маршруты через этот город: сезоны закрываются на вкладке «Preturi», в разделе «Сезонные тарифы».",

  "server.label": "Сообщение сервера (на румынском):",

  "action.openPrices": "Открыть «Preturi»",
  "action.openRoute": "Открыть маршрут #{id}",
  "action.close": "Закрыть",
  "action.closeAria": "Закрыть сообщение",

  "generic.error": "Произошла ошибка. Попробуйте ещё раз.",
  "generic.network": "Не удалось связаться с сервером. Проверьте соединение и попробуйте ещё раз.",
};

const en: SeasonRefusalCatalog = {
  "title.stop": "The stop was not deleted",
  "title.stopNamed": "The stop “{name}” was not deleted",
  "title.route": "The route was not deleted",
  "title.routeNamed": "The route “{name}” was not deleted",
  "title.city": "The city was not deleted",
  "title.cityNamed": "The city “{name}” was not deleted",
  "title.category": "The separate category was not created",
  "title.categoryNamed": "The separate category for the route “{name}” was not created",

  "text.stop": "The stop has fares in an active season. Close the season first, then delete the stop. Nothing was changed.",
  "text.route": "The route has fares in an active season. Close the season first, then delete the route. Nothing was changed.",
  "text.city": "The city is a stop on a route with fares in an active season. Close the season first, then delete the city. Nothing was changed.",
  "text.category": "The category has fares in an active season on this route. Close the season first, then create the separate category. Nothing was changed.",
  "text.retry": "The server is busy. Please try again. Nothing was changed.",
  "text.scope": "The route changed in the meantime. Reload the page and try again. Nothing was changed.",

  "seasons.list": "Active seasons: {list}",
  "seasons.more": "{list} and {count} more",

  "hint.stop": "Seasons are closed in the “Preturi” tab, “Seasonal fares” section.",
  "hint.route": "Open the route: seasons are closed in the “Preturi” tab, “Seasonal fares” section.",
  "hint.city": "Open the routes through this city: seasons are closed in the “Preturi” tab, “Seasonal fares” section.",

  "server.label": "Server message (in Romanian):",

  "action.openPrices": "Open “Preturi”",
  "action.openRoute": "Open route #{id}",
  "action.close": "Close",
  "action.closeAria": "Close message",

  "generic.error": "Something went wrong. Please try again.",
  "generic.network": "The server could not be reached. Check the connection and try again.",
};

export const SEASON_REFUSAL_MESSAGES: Record<SeasonRefusalLocale, SeasonRefusalCatalog> = {ro, ru, en};

export const translateSeasonRefusal = (
  locale: SeasonRefusalLocale,
  key: SeasonRefusalMessageKey,
  params: Record<string, string | number> = {},
): string =>
  SEASON_REFUSAL_MESSAGES[locale][key].replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match);

/** Seasons listed by number; beyond this many, "and N more". */
export const MAX_LISTED_SEASONS = 10;

/** The application default when no language is stored (the plugin is created with `en`). */
const APP_DEFAULT_LOCALE = "en";

const storedLocale = (): string => {
  try {
    return (typeof localStorage !== "undefined" && localStorage.getItem("lang")) || APP_DEFAULT_LOCALE;
  } catch (_) {
    return APP_DEFAULT_LOCALE;
  }
};

/** The UI locale outside a component (the stored choice). */
export const currentSeasonRefusalLocale = (): SeasonRefusalLocale => resolveSeasonRefusalLocale(storedLocale());

/** What one refusal reads like, ready for the alert (every field is plain text). */
export interface SeasonRefusalView {
  key: number;
  code: string;
  title: string;
  /** The main sentence; `textLang` is its language (the server writes Romanian). */
  text: string;
  textLang: SeasonRefusalLocale;
  /** "Active seasons: #7, #9", or `null` when the server named none. */
  seasons: string | null;
  /** The server's Romanian sentence under a RU/EN text; `null` in RO (it IS the text). */
  server: string | null;
  serverLabel: string;
  /** Where the seasons are closed; `null` for the busy / changed codes. */
  hint: string | null;
}

type Kind = "stop" | "route" | "city" | "category" | "retry" | "scope";

function kindOf(refusal: SeasonRefusal): Kind {
  if (refusal.code === "fare_category_has_active_price_window") return "category";
  if (refusal.code === "pricing_mutation_retry_exhausted") return "retry";
  if (refusal.code === "pricing_scope_changed") return "scope";
  return refusal.surface === "route" || refusal.surface === "city" ? refusal.surface : "stop";
}

export function describeSeasonRefusal(refusal: SeasonRefusal, locale: SeasonRefusalLocale): SeasonRefusalView {
  const t = (key: SeasonRefusalMessageKey, params: Record<string, string | number> = {}) => translateSeasonRefusal(locale, key, params);
  const kind = kindOf(refusal);
  const surface = refusal.surface ?? (kind === "category" ? "category" : kind === "route" || kind === "city" ? kind : "stop");
  const name = refusal.subjectName;
  const title = name ? t(`title.${surface}Named` as SeasonRefusalMessageKey, {name}) : t(`title.${surface}` as SeasonRefusalMessageKey);

  const localized = t(`text.${kind}` as SeasonRefusalMessageKey);
  const useServer = locale === "ro" && refusal.serverMessage !== null;

  let seasons: string | null = null;
  if (refusal.windowIds.length) {
    const shown = refusal.windowIds.slice(0, MAX_LISTED_SEASONS).map((id) => `#${id}`).join(", ");
    const rest = refusal.windowIds.length - MAX_LISTED_SEASONS;
    seasons = t("seasons.list", {list: rest > 0 ? t("seasons.more", {list: shown, count: rest}) : shown});
  }

  const hintKey = kind === "stop" ? "hint.stop" : kind === "city" ? "hint.city" : kind === "route" || kind === "category" ? "hint.route" : null;
  return {
    key: refusal.key,
    code: refusal.code,
    title,
    text: useServer ? (refusal.serverMessage as string) : localized,
    textLang: useServer ? "ro" : locale,
    seasons,
    server: locale !== "ro" ? refusal.serverMessage : null,
    serverLabel: t("server.label"),
    hint: hintKey ? t(hintKey) : null,
  };
}

export interface SeasonRefusalsI18n {
  locale: ComputedRef<SeasonRefusalLocale>;
  t: (key: SeasonRefusalMessageKey, params?: Record<string, string | number>) => string;
  describe: (refusal: SeasonRefusal) => SeasonRefusalView;
}

export const useSeasonRefusalsI18n = (): SeasonRefusalsI18n => {
  const i18n = getCurrentInstance()?.appContext.config.globalProperties.$i18n as {locale?: unknown} | undefined;
  const locale = computed(() => {
    const app = i18n ? unref(i18n.locale) : undefined;
    return resolveSeasonRefusalLocale(app ? app : storedLocale());
  });
  return {
    locale,
    t: (key, params = {}) => translateSeasonRefusal(locale.value, key, params),
    describe: (refusal) => describeSeasonRefusal(refusal, locale.value),
  };
};
